import { useState } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../v32/components/ui/card';
import { getAdminMetrics, runWeeklyJobOnce } from 'wasp/client/operations';
import type { AdminMetrics as AdminMetricsType } from '../../types/admin';

type TimeframePreset = '7d' | '28d' | '90d' | 'all';

export default function AdminMetrics() {
  const [preset, setPreset] = useState<TimeframePreset>('28d');
  const { data: metricsRaw, isLoading, error, refetch } = useQuery(getAdminMetrics, { preset });
  const metrics = (metricsRaw as unknown) as AdminMetricsType;
  const runJob = useAction(runWeeklyJobOnce);

  const handleRecompute = async () => {
    try {
      await runJob({});
      setTimeout(() => refetch(), 1000); // Refetch after job completes
    } catch (error) {
      console.error('Failed to trigger job:', error);
    }
  };

  const isDevelopment = import.meta.env.DEV;

  if (isLoading) return <div className="p-4 text-sm text-neutral-600">Loading metrics...</div>;
  if (error) return <div className="p-4 text-sm text-error-600">Error: {error.message}</div>;
  if (!metrics) return <div className="p-4 text-sm text-neutral-600">No metrics available</div>;

  return (
    <div className="space-y-6">
      {/* Sticky Subheader */}
      <div className="sticky top-0 z-10 bg-surface/80 backdrop-blur border-b border-black/5 px-4 py-2 flex items-center justify-between rounded-lg">
        <div className="flex items-center gap-4">
          <span className="text-xs text-muted">
            Updated {new Date(metrics.generatedAt).toLocaleTimeString()}
          </span>
          
          {/* Timeframe Picker */}
          <div className="flex items-center gap-1">
            {(['7d', '28d', '90d', 'all'] as const).map((option) => (
              <button
                key={option}
                onClick={() => setPreset(option)}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  preset === option
                    ? 'bg-coral-100 text-coral-700 border border-coral-200'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                {option === 'all' ? 'All Time' : option.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isDevelopment && (
            <button 
              onClick={handleRecompute}
              className="text-xs border border-sage-200 px-2 py-1 rounded-md hover:bg-sage-50 hover:text-sage-700 transition-colors"
            >
              Recompute Now
            </button>
          )}
          <button 
            onClick={() => refetch()} 
            className="text-xs border border-coral-200 px-2 py-1 rounded hover:bg-coral-50 hover:text-coral-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* PMF Overview */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">Product-Market Fit Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="4-Week Retention"
            value={`${metrics.pmf.fourWeekRetentionPct}%`}
            target={`${metrics.pmf.fourWeekRetentionTargetPct}%`}
            trend={metrics.pmf.fourWeekRetentionTrend}
          />
          <MetricCard
            title="Trial Conversion"
            value={`${metrics.pmf.trialConversionPct}%`}
            target={`${metrics.pmf.trialConversionTargetPct}%`}
            trend={metrics.pmf.trialConversionTrend}
          />
          <MetricCard
            title="Weekly Churn"
            value={`${metrics.pmf.weeklyChurnPct}%`}
            target={`${metrics.pmf.weeklyChurnTargetPct}%`}
            trend={metrics.pmf.weeklyChurnTrend}
            isReversed
          />
          <MetricCard
            title="Monthly Recurring Revenue"
            value={`$${metrics.pmf.mrrUSD.toLocaleString()}`}
            target={`$${metrics.pmf.mrrTargetUSD.toLocaleString()}`}
            trend={metrics.pmf.mrrTrend}
          />
        </div>
      </section>

      {/* AI Usage */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">AI Usage Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-surface border border-black/5 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Weekly Tokens</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-sage-700">{metrics.ai.weeklyTokens.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-surface border border-black/5 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Avg Cost per User</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-coral-700">${metrics.ai.avgCostPerUserUSD.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="bg-surface border border-black/5 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Generation Success</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-sage-700">{metrics.ai.generationSuccessPct}%</div>
            </CardContent>
          </Card>
          <Card className="bg-surface border border-black/5 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Upgrade Conversion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold text-sage-700">{metrics.ai.upgradeConversionPct}%</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Costs by Tier */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">Cost Analysis by Tier</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics.costsByTier.map((tier: AdminMetricsType['costsByTier'][number]) => (
            <Card key={tier.tier} className="bg-surface border border-black/5 shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-neutral-600">{tier.tier}</CardTitle>
                <CardDescription>
                  <span className="text-sage-700 bg-sage-50 border border-sage-100 text-xs px-2 py-0.5 rounded-md">
                    {tier.users} users
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold text-coral-700">${tier.avgCostUSD.toFixed(2)}</div>
                <div className="text-sm text-neutral-500 mt-1">avg cost</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Phase Checklist */}
      <section>
        <h2 className="text-lg font-semibold mb-4 text-neutral-800">Phase 1: PMF Checklist</h2>
        <Card className="bg-surface border border-black/5 shadow-card">
          <CardContent className="p-6">
            <div className="space-y-3">
              {metrics.checklist.map((item: AdminMetricsType['checklist'][number], index: number) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-neutral-50">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                      item.met 
                        ? 'bg-sage-100 text-sage-700' 
                        : 'bg-neutral-200 text-neutral-500'
                    }`}>
                      {item.met && '✓'}
                    </div>
                    <span className={`text-sm ${item.critical ? 'font-medium' : ''} ${
                      item.met ? 'text-sage-800' : 'text-neutral-600'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  {item.critical && (
                    <span className="text-xs px-2 py-1 bg-coral-100 text-coral-700 rounded">
                      Critical
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <div className="text-xs text-neutral-500 text-center py-4">
        Generated at {new Date(metrics.generatedAt).toLocaleString()}
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  target: string;
  trend: 'up' | 'down' | 'flat';
  isReversed?: boolean; // for metrics where "down" is good (like churn)
}

function MetricCard({ title, value, target, trend, isReversed = false }: MetricCardProps) {
  const getTrendColor = (trend: 'up' | 'down' | 'flat', reversed: boolean) => {
    if (trend === 'flat') return 'text-neutral-500';
    if (reversed) {
      return trend === 'up' ? 'text-error-600' : 'text-success-600';
    }
    return trend === 'up' ? 'text-success-600' : 'text-error-600';
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'flat') => {
    if (trend === 'up') return '↗';
    if (trend === 'down') return '↘';
    return '→';
  };

  // Extract numeric values for comparison
  const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
  const numericTarget = parseFloat(target.replace(/[^\d.-]/g, ''));
  
  // Determine if target is met (reversed logic for churn-type metrics)
  const targetMet = isReversed ? numericValue <= numericTarget : numericValue >= numericTarget;
  
  // KPI color logic: saffron/olive based on target met
  const valueColor = targetMet ? 'text-sage-700' : 'text-coral-700';

  return (
    <Card className="bg-surface border border-black/5 shadow-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-neutral-600">{title}</CardTitle>
        <CardDescription className="text-xs">Target: {target}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between">
          <div className={`text-2xl font-semibold ${valueColor}`}>{value}</div>
          <div className={`text-sm font-medium ${getTrendColor(trend, isReversed)}`}>
            {getTrendIcon(trend)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
